import { useEffect, useState } from 'react';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';

const CACHE_PREFIX = 'alea_ds_v1:';
const MAX_CACHE_CHARS = 400_000;

function scopeFromAdapter(adapter: { activeHouseId?: string; houseId?: string; name?: string } | null) {
  if (!adapter) return 'none';
  return String(adapter.activeHouseId ?? adapter.houseId ?? adapter.name ?? 'default');
}

function cacheKey(adapter: unknown, collection: string) {
  return `${CACHE_PREFIX}${scopeFromAdapter(adapter as { activeHouseId?: string })}:${collection}`;
}

function readCached<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeCached(key: string, data: unknown) {
  try {
    const raw = JSON.stringify(data);
    if (raw.length > MAX_CACHE_CHARS) return;
    sessionStorage.setItem(key, raw);
  } catch {
    // Quota / private mode — ignore.
  }
}

type SyncAdapter = Pick<IDataAdapter, 'subscribe'> & {
  activeHouseId?: string;
  houseId?: string;
  name?: string;
};

/**
 * Subscribe to an adapter collection, hydrating from sessionStorage for instant
 * paint on slow/offline reconnects (Firestore persistence may still catch up).
 */
export function useDataSync<T>(
  adapter: SyncAdapter | null | undefined,
  collection: string,
  initialValue: T,
): T {
  const key = cacheKey(adapter ?? null, collection);
  const [data, setData] = useState(() => (
    adapter ? readCached(key, initialValue) : initialValue
  ));

  useEffect(() => {
    if (!adapter) return undefined;
    const storageKey = cacheKey(adapter, collection);
    setData(readCached(storageKey, initialValue));
    return adapter.subscribe<T>(collection, (next) => {
      setData(next);
      writeCached(storageKey, next);
    });
  // initialValue is a default seed; callers pass literals — omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, collection]);

  return data;
}
